import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  signal,
  computed
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { LoggingService } from '@newroom-connect/library/services';

import { MarzipanoScene } from '../../models/marzipano-scene.model';
import { CubemapFaces, IMarzipanoLoadingProgress } from '../../models/marzipano-cubemap-loader.model';
import { IMarzipanoViewState, IMarzipanoSceneConfig } from '../../interfaces/marzipano-scene.interface';
import { SceneEvent } from '../../interfaces/scene.interface';

/**
 * Simplified Marzipano 360° scene component.
 *
 * This component provides a clean, minimal interface for loading and displaying
 * Marzipano cubemap scenes. It focuses solely on scene management and delegates
 * hotspot functionality to external adapters.
 *
 * @example
 * ```html
 * <nrc-marzipano-scene
 *   [faces]="cubemapFaces"
 *   [config]="sceneConfig"
 *   [initialView]="viewState"
 *   (sceneReady)="onSceneReady($event)"
 *   (loadingProgress)="onProgress($event)"
 *   (sceneError)="onError($event)">
 * </nrc-marzipano-scene>
 * ```
 */
@Component({
  selector: 'nrc-marzipano-scene',
  standalone: true,
  templateUrl: './marzipano-scene.component.html',
  styleUrl: './marzipano-scene.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarzipanoSceneComponent implements AfterViewInit, OnChanges, OnDestroy {
  @HostBinding('class') public hostClass = 'marzipano-scene-container block w-full h-full';

  @ViewChild('container', { static: true }) private containerRef!: ElementRef<HTMLDivElement>;

  @Input({ required: true }) public faces!: CubemapFaces;
  @Input() public config?: IMarzipanoSceneConfig;
  @Input() public initialView?: Partial<IMarzipanoViewState>;

  @Output() public sceneReady = new EventEmitter<MarzipanoScene>();
  @Output() public loadingProgress = new EventEmitter<IMarzipanoLoadingProgress>();
  @Output() public viewChanged = new EventEmitter<IMarzipanoViewState>();
  @Output() public sceneError = new EventEmitter<Error>();

  private readonly logger = inject(LoggingService);
  private readonly marzipanoScene = new MarzipanoScene();
  private readonly destroy$ = new Subject<void>();
  private isInitialized = false;

  // Loading state signals
  public readonly loadingProgressSignal = signal<IMarzipanoLoadingProgress | null>(null);
  public readonly errorSignal = signal<string | null>(null);
  public readonly faceNames = ['px', 'nx', 'py', 'ny', 'pz', 'nz'] as const;

  // Computed signals
  public readonly isLoading = computed(() => {
    const progress = this.loadingProgressSignal();

    return progress?.status === 'loading';
  });

  public readonly hasError = computed(() => this.errorSignal() !== null);

  /**
   * Get direct access to the scene instance for hotspot adapters.
   *
   * @returns The MarzipanoScene instance or null if not ready.
   */
  public get scene(): MarzipanoScene | null {
    return this.marzipanoScene.isInitialized ? this.marzipanoScene : null;
  }

  /**
   * Initialize the scene after view initialization.
   */
  public async ngAfterViewInit(): Promise<void> {
    try {
      this.setupEventListeners();
      await this.initializeScene();

      // Set initial view if provided
      if (this.initialView) {
        this.marzipanoScene.setViewState(this.initialView);
      }

      // Load cubemap
      await this.loadCubemap();

      this.isInitialized = true;
      this.sceneReady.emit(this.marzipanoScene);

      this.logger.info('Marzipano scene component initialized successfully');
    } catch (error) {
      this.logger.error('Failed to initialize Marzipano scene component', error as Error);
      this.sceneError.emit(error as Error);
    }
  }

  /**
   * Handle input changes, particularly face map updates.
   *
   * @param changes - The changes to component inputs.
   */
  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    // Handle face map changes after initialization
    if (changes['faces'] && !changes['faces'].isFirstChange() && this.isInitialized) {
      try {
        await this.loadCubemap();
      } catch (error) {
        this.logger.error('Failed to load new cubemap after faces change', error as Error);
        this.sceneError.emit(error as Error);
      }
    }

    // Handle initial view changes
    if (changes['initialView'] && !changes['initialView'].isFirstChange() && this.isInitialized) {
      if (this.initialView) {
        this.marzipanoScene.setViewState(this.initialView);
      }
    }
  }

  /**
   * Clean up resources when component is destroyed.
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.marzipanoScene.isInitialized) {
      this.marzipanoScene.dispose();
    }

    this.logger.debug('Marzipano scene component destroyed');
  }

  /**
   * Set the view state programmatically.
   *
   * @param viewState - The view state to apply.
   */
  public setViewState(viewState: Partial<IMarzipanoViewState>): void {
    if (this.marzipanoScene.isInitialized) {
      this.marzipanoScene.setViewState(viewState);
    } else {
      this.logger.warn('Cannot set view state: scene not initialized');
    }
  }

  /**
   * Retrieve the current camera position and orientation.
   *
   * @returns Current camera state or null if scene not ready.
   */
  public getCurrentCameraState(): IMarzipanoViewState | null {
    return this.marzipanoScene.isInitialized ? this.marzipanoScene.getCurrentCameraState() : null;
  }

  /**
   * Animate to a specific view state.
   *
   * @param viewState - The target view state.
   * @param duration - Animation duration in milliseconds (default: 1000).
   * @returns Promise that resolves when animation completes.
   */
  public async animateToViewState(viewState: Partial<IMarzipanoViewState>, duration = 1000): Promise<void> {
    if (this.marzipanoScene.isInitialized) {
      await this.marzipanoScene.animateToViewState(viewState, duration);
    } else {
      this.logger.warn('Cannot animate view state: scene not initialized');
    }
  }

  /**
   * Initialize the Marzipano scene with the container element.
   */
  private async initializeScene(): Promise<void> {
    const container = this.containerRef.nativeElement;

    await this.marzipanoScene.initialize(container, this.config);
  }

  /**
   * Load the cubemap from the current faces.
   */
  private async loadCubemap(): Promise<void> {
    if (!this.marzipanoScene.isInitialized) {
      throw new Error('Scene not initialized');
    }

    await this.marzipanoScene.loadCubemap(this.faces);
  }

  /**
   * Set up event listeners for scene events.
   */
  private setupEventListeners(): void {
    // Loading progress updates
    this.marzipanoScene.loadingProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe(progress => {
        this.loadingProgressSignal.set(progress);
        this.loadingProgress.emit(progress);
      });

    // View state changes
    this.marzipanoScene.on(SceneEvent.VIEW_CHANGE)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.marzipanoScene.isInitialized) {
          const viewState = this.marzipanoScene.getCurrentCameraState();

          this.viewChanged.emit(viewState);
        }
      });

    // Error events
    this.marzipanoScene.on(SceneEvent.ERROR)
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        const error = event.data instanceof Error ? event.data : new Error('Scene error occurred');

        this.errorSignal.set(error.message);
        this.logger.error('Scene error event received', error);
        this.sceneError.emit(error);
      });
  }
}
